import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import LeaderBoardTop from "@/components/molecules/leader-board/leader-board-top";
import LeaderBoardItem from "@/components/molecules/leader-board/leader-board-item";
import OwnerLeaderBoardItem from "@/components/molecules/leader-board/owner-leader-board-item";
import {
  LeaderboardItemType,
  LeaderboardMetric,
  MenuType,
} from "@/types/leader-board";
import {
  MENU_CONFIG,
  TOP_POSITION_COLORS,
  LEADERBOARD_CONSTANTS,
  LEADERBOARD_TEXT,
} from "@/constants/leaderboard";
import LeaderboardSkeleton from "@/components/atomics/leaderboard-skeleton";
import LoadMoreButton from "@/components/atomics/load-more-button";

interface LeaderBoardTableProps {
  data: LeaderboardItemType[];
  myData: LeaderboardItemType | null;
  selectedMenu: MenuType;
  metric: LeaderboardMetric;
  onRefetchData?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
}

export default function LeaderBoardTable({
  data,
  myData,
  selectedMenu,
  metric,
  onRefetchData,
  onLoadMore,
  hasMore,
  isLoading,
}: LeaderBoardTableProps) {
  const navigate = useNavigate();
  const [backgroundImage, setBackgroundImage] = useState<string>(
    MENU_CONFIG.users.backgroundImage
  );
  const [activeShareItem, setActiveShareItem] = useState<string | null>(null);

  const formatValue = useCallback(
    (value: number | string): string => {
      const numeric = Number(value) || 0;

      if (selectedMenu === "nft") {
        return numeric.toString();
      }

      if (metric === "volume") {
        return numeric.toFixed(2);
      }

      return Math.floor(numeric).toString();
    },
    [selectedMenu, metric]
  );

  const handleItemClick = useCallback(
    (address: string) => {
      navigate(`/owner/${address}`);
    },
    [navigate]
  );

  const handleMiddlePartClick = useCallback(
    (address: string) => {
      setActiveShareItem(activeShareItem === address ? null : address);
    },
    [activeShareItem]
  );

  useEffect(() => {
    setBackgroundImage(MENU_CONFIG[selectedMenu].backgroundImage);
  }, [selectedMenu]);

  const topThreeItems = useMemo(() => {
    if (
      !data ||
      data.length < LEADERBOARD_CONSTANTS.MIN_ITEMS_FOR_TOP_POSITIONS
    ) {
      return [];
    }
    return data.slice(0, 3);
  }, [data]);

  const remainingItems = useMemo(() => {
    if (
      !data ||
      data.length <= LEADERBOARD_CONSTANTS.MIN_ITEMS_FOR_TOP_POSITIONS
    ) {
      return [];
    }
    return data.slice(LEADERBOARD_CONSTANTS.MIN_ITEMS_FOR_TOP_POSITIONS);
  }, [data]);

  const minItemsRequired = LEADERBOARD_CONSTANTS.MIN_ITEMS_FOR_TOP_POSITIONS;

  if (!data || data.length < minItemsRequired) {
    return (
      <div className="relative w-full flex">
        <img
          src={backgroundImage}
          alt="leaderboard background"
          className="mt-[30px]  left-0 -z-[1] object-top object-none"
          loading="lazy"
        />
        <div className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-center">
          No data
        </div>
      </div>
    );
  }

  const renderTopPosition = (
    item: LeaderboardItemType,
    position: 1 | 2 | 3,
    className?: string
  ) => (
    <LeaderBoardTop
      className={className || "w-[35%]"}
      selectedMenu={selectedMenu}
      bgColor={TOP_POSITION_COLORS[position]}
      image={item.avatar}
      name={item.name}
      rank={item.rank?.toString() || position.toString()}
      value={formatValue(item.value)}
      ownerAddress={item.ownerAddress}
      indexInCollection={item.indexInCollection}
      metric={metric}
      address={item.address}
      onRefetchData={onRefetchData}
    />
  );

  return (
    <div className="w-full flex flex-col items-center mt-[30px] relative">
      <div className="w-full flex">
        <img
          src={backgroundImage}
          alt="leaderboard background"
          className="mt-[30px] absolute left-0 -z-[1] object-top object-none"
          loading="lazy"
        />
      </div>

      {topThreeItems[0] && renderTopPosition(topThreeItems[0], 1)}

      {topThreeItems[1] && topThreeItems[2] && (
        <div className="w-full flex justify-center gap-[40px] pt-[26px]">
          {renderTopPosition(topThreeItems[1], 2)}
          {renderTopPosition(topThreeItems[2], 3)}
        </div>
      )}

      {myData && (
        <div className="w-full pt-[20px] pb-[10px]">
          <OwnerLeaderBoardItem
            rank={myData.rank}
            name={myData.name}
            value={myData.value}
            address={myData.address}
            avatar={myData.avatar}
            ownerAddress={myData.ownerAddress}
            selectedMenu={selectedMenu}
            metric={metric}
            indexInCollection={myData.indexInCollection}
          />
        </div>
      )}

      {remainingItems.map(item => (
        <LeaderBoardItem
          key={`${item.rank}-${item.address}`}
          rank={item.rank}
          name={item.name}
          value={item.value}
          address={item.address}
          avatar={item.avatar}
          ownerAddress={item.ownerAddress}
          indexInCollection={item.indexInCollection}
          selectedMenu={selectedMenu}
          metric={metric}
          onClick={() => handleItemClick(item.address)}
          onRefetchData={onRefetchData}
          showShareButton={activeShareItem === item.address}
          onMiddlePartClick={() => handleMiddlePartClick(item.address)}
        />
      ))}

      {onLoadMore && (
        <LoadMoreButton
          isLoading={isLoading || false}
          hasMore={hasMore || false}
          onLoadMore={onLoadMore}
        />
      )}
    </div>
  );
}
